// Module 10 — the Cesium adapter mechanics. mount builds the viewer (offline imagery + flat terrain,
// no ion) + ONE PolylineCollection (one polyline per edge along its true OSM polyline, all sharing ONE
// Fabric material) + node point primitives at true coords, then SUBSCRIBES to the state store and wires
// a LEFT_CLICK pick. applySnapshot pushes into the store; the subscription restyles edges from
// branchFlows, applies per-layer flow overlay (capped by quality), mutates the flow material time in
// place, and hands the v2 prosumers/loads (read via selProsumers/selLoads) to the effects encoder.
// Picks publish to state ONLY. setQuality is fidelity-only. setBasemap is the lone network path.
// Boundary: imports only `domain`, `state`, and same-module files (CLAUDE.md #2).
import { selFlows, selProsumers, selLoads } from "../state/index";
import type { Store, UiState } from "../state/index";
import type { Layer, Snapshot, FlowSample } from "../domain/index";
import type {
  CesiumLike, ViewerLike, PolylineCollectionLike, PointPrimitiveCollectionLike,
  MaterialLike, ScreenSpaceEventHandlerLike, PolylineLike, PointPrimitiveLike, Cesium3DTilesetLike,
} from "./cesium-api";
import type { CesiumAdapter, VizModel, QualityTier, BasemapKind, PickSel, EffectsEncoder } from "./contract";
import { nodeLonLat, edgeDegreesArray, edgeRenderPolyline, isRenderableNode } from "./geometry";
import { makeFootprintLayer } from "./footprints";
import type { FootprintLayer } from "./footprints";
import { sunDate, formatSunDate } from "./sun-clock";
import type { SunDate } from "./sun-clock";
import { makeOverlayArcLayer, arcEndpoints } from "./overlay-arcs";
import type { OverlayArcLayer } from "./overlay-arcs";
import { ballisticArc } from "./arc-geometry";
import { EdgeFlowStateMachine } from "./edge-flow-state";
import { makeWallLayer } from "./walls";
import type { WallLayer } from "./walls";
import { makePlantLayer } from "./plant-columns";
import type { PlantLayer } from "./plant-columns";
import { makeProsumerLayer } from "./prosumer-bars";
import type { ProsumerLayer } from "./prosumer-bars";
import { makeNodeAssetLayer } from "./node-assets";
import type { NodeAssetLayer } from "./node-assets";
import { makeNodeVoltageLayer } from "./node-voltage";
import type { NodeVoltageLayer } from "./node-voltage";
import { makeFlowMaterial, makeStaticMaterial, makeInServiceMaterial, makeSelectionMaterial, setFlowTime, bandOf, TIER_COLOR, mixHex } from "./flow-material";
import { islandColor } from "./effects/palette";
import { makeFlowGlyphLayer, type FlowGlyphLayer, type GlyphEdge, type GlyphActive } from "./flow-glyphs";
import { buildImageryProvider } from "./imagery";
import { resolvePick } from "./picking";
import { SelectionController } from "./selection-controller";
import { makeParcelResolver } from "./parcel-crosswalk";
import { makeFacilityParcelLayer } from "./facility-parcels";
import type { FacilityParcelLayer, FacilityParcel } from "./facility-parcels";
import type { SceneSpec, AssetStatus, CrisisBucket } from "./effects/spec";
import { tierKnobs } from "./effects/quality";

const LAYERS: readonly Layer[] = ["energy", "information", "finance"];
// Phase 2.6: a small epsilon lift when a 3D-tiles basemap is active — just enough to avoid z-fight
// with the tileset surface (was 600m "float above the CN Tower"; now the grid sits ON the ground,
// with depthTestAgainstTerrain handling the occlusion). 0 (identity) for the flat 2D basemaps.
const GRID_LIFT_3D_M = 0.1;
// Information + finance overlays render as BALLISTIC ARCS (arc-geometry.ts) that arch high off the flat
// electrical-edge plane in BRIGHT BLUE — so they never mix with the energy flows on the cables. The
// moving transaction arrows ride a CONTRASTING colour (line ≠ arrows), and finance is distinct from
// info: deeper-blue line + higher arch + gold arrows (money) vs bright-cyan line + white-cyan arrows.
const INFO_ARC = { line: "#33C9FF", arrow: "#EAFBFF", apexScale: 0.12 };
// The retail bill settles in THREE legs (build log 47), each routed to a distinct upstream counterparty
// and drawn as its own glowing ballistic arc + "$"-badged arrows — so "where the money goes" reads at a
// glance: commodity → generators (gold, low arch), transmission → Hydro One stations (amber, mid arch),
// distribution → the LDC substation (teal, higher arch). `match` also folds the sparse P2P trade + any
// untagged (back-compat) finance flow into the commodity leg.
const FINANCE_LEGS: ReadonlyArray<{
  kind: "commodity" | "transmission" | "distribution"; line: string; arrow: string; apexScale: number;
  match: (k: FlowSample["kind"]) => boolean;
}> = [
  { kind: "commodity", line: "#FFD54F", arrow: "#FFF8E1", apexScale: 0.10, match: (k) => k === "commodity" || k === "p2p" || k === undefined },
  { kind: "transmission", line: "#E69F00", arrow: "#FFE0B2", apexScale: 0.15, match: (k) => k === "transmission" },
  { kind: "distribution", line: "#26C6DA", arrow: "#B2EBF2", apexScale: 0.20, match: (k) => k === "distribution" },
];
// Phase 0.2: the duplicate FLOW_CAP constant is DELETED — applyFlows now reads the SINGLE central
// table tierKnobs(quality).flowCap (effects/quality.ts), the same table the governor drives. DRY fix.

// Non-contract display-fidelity extras (v3): Debug 3 components the SOLVER model omits but the map
// must show — facility parcels (luminous walls), connection points, and the wiring the stub-prune
// removed. Injected by the app via createCesiumAdapter opts (the same non-contract seam as
// `generators`); the frozen VizModel is untouched.
export interface DisplayExtras {
  facilityParcels?: ReadonlyArray<{
    id: string; name: string; facType: string; nominalKv: number; vTier: string;
    ring: ReadonlyArray<{ lat: number; lon: number }>;
  }>;
  connectionPoints?: ReadonlyArray<{ id: string; coord: { lat: number; lon: number } }>;
  prunedLines?: ReadonlyArray<{
    id: string; vTier: string; kind: string; kv: number;
    polyline: ReadonlyArray<{ lat: number; lon: number }>;
  }>;
}

// Non-contract introspection seam for tests + the app governor. NOT part of frozen viz.v1.
export interface VizDebug {
  readonly _debug: {
    readonly lastSnapshot: Snapshot | null;
    readonly quality: QualityTier;
    // perf-HUD readout: real drawing-buffer pixels + the buffer-px-per-CSS-px renderScale + device dpr
    renderResolution(): { width: number; height: number; renderScale: number; dpr: number };
    readonly basemap: BasemapKind;
    readonly edgeCount: number;
    readonly layersVisible: Readonly<Record<Layer, boolean>>;
    // deterministic camera jump for support/verification tooling (the app exposes it on window.__gta)
    lookAt(lon: number, lat: number, heightM: number): void;
    // timed scene.render() loop — REAL per-frame render cost, measurable even in a hidden tab
    frameMs(samples?: number): number;
    // show/hide the three effects primitives (walls/plants/bars) — the perf-bisect lever
    setEffectsShow(visible: boolean): void;
    // P14 live-verification: the histogram of material `type`s actually applied to the edge polylines on
    // the live map (proves the loading-danger band / grey OFF / selection materials render), + the selected
    // edge id and the count of shown danger-halo billboards. (non-contract introspection seam)
    edgeMaterials(): { tally: Record<string, number>; selected: string | null; haloShown: number; haloTotal: number };
    readonly flowTime: number; // current animated-flow clock (seconds); -1 before mount
    // Batch C-2 date/sun seam (non-contract) — the index.html clock UI reads simDate + drives these.
    readonly simDate: (SunDate & { label: string }) | null; // calendar instant the sun is using
    readonly displayYear: number;
    setDisplayYear(year: number): void; // re-stamp the calendar year (cosmetic) + re-push the sun
    setLighting(on: boolean): void; // toggle globe sun-lighting without touching the clock
    setVoltageOverlay(on: boolean): void; // Phase 1.1: toggle node-point recolour by live busVMagPu
    readonly voltageOverlay: boolean;
    readonly gridLifted: boolean; // Batch C float-above: is the grid currently floated over 3D tiles?
  };
}

// Non-contract factory: real Cesium + the store are injected. index.ts binds them for production;
// tests inject a recording fake. The contract entry the app uses is createCesiumAdapter().
export function makeAdapter(
  cesium: CesiumLike,
  store: Store<UiState>,
  effects?: EffectsEncoder,
  display?: DisplayExtras,
): CesiumAdapter & VizDebug {
  let viewer: ViewerLike | null = null;
  // one PolylineCollection + one tinted flow material PER VOLTAGE TIER present in the model —
  // the "types of power grids" channel (lazily created; small fixture models stay single-collection)
  const edgeCollections = new Map<string, PolylineCollectionLike>();
  // P14 loading-danger RAMP: the chevron + idle materials are keyed by loading BAND too, so an overloaded
  // edge glows warn/hot/red over its tier hue. Base-band variants are built eagerly at mount (so the count
  // is stable); warn/hot/red are built LAZILY on first use (the steady fixture stays at the base count).
  const flowMaterials = new Map<string, MaterialLike>(); // `${tier}:${band}:F|R` marching chevron
  const staticMaterials = new Map<string, MaterialLike>(); // `${tier}` dead GREY no-arrow (de-energized / out of service)
  const idleMaterials = new Map<string, MaterialLike>(); // `${tier}:${band}` SOLID no-arrow (in service, ~0 flow this tick)
  let selectionMat: MaterialLike | null = null; // P14 edge-selection bright highlight (built lazily on first select)
  let selectedEdgeId: string | null = null; // the one edge whose polyline currently rides the selection material
  let edgeLines: PolylineCollectionLike | null = null; // kept: the FIRST tier collection (legacy name)
  let arcLines: PolylineCollectionLike | null = null; // pooled overlay arcs (info/finance synthetic ids)
  let infoArcs: OverlayArcLayer | null = null;
  // engine-v2 marching ARROW-GLYPH layers (the user's "large, recognizable moving chevron arrows").
  // energyGlyphs: static geometry (model edges), big tier-coloured arrows on energized edges. info/
  // finance: smaller arrows along the elevated overlay arcs (rebuilt per snapshot from the samples).
  let energyGlyphs: FlowGlyphLayer | null = null;
  let infoGlyphs: FlowGlyphLayer | null = null;
  // 3 finance settlement legs (commodity/transmission/distribution): each = a glowing arc + badged glyphs.
  let financeLegs: { arcs: OverlayArcLayer; glyphs: FlowGlyphLayer; cfg: (typeof FINANCE_LEGS)[number] }[] = [];
  const edgePolyById = new Map<string, readonly (readonly [number, number])[]>(); // glyph track geometry
  let markerPoints: PointPrimitiveCollectionLike | null = null; // selection ring (own collection)
  // v3 display extras (pruned wiring / connection points / facility walls) are STATIC primitives
  // owned by the scene — viewer.destroy() tears them down; no per-tick handles are kept.
  let animId = 0; // rAF id of the smooth animation clock (browser only; node tests never start it)
  const nodePos = new Map<string, readonly [number, number]>(); // ALL nodes (junctions too) — arc endpoints
  const posOf = (id: string): readonly [number, number] | undefined => nodePos.get(id);
  let nodePoints: PointPrimitiveCollectionLike | null = null;
  let flowMaterial: MaterialLike | null = null;
  let wallLayer: WallLayer | null = null; // hollow extruded neighbourhood walls (10.6 SceneSpec applier)
  let footprintLayer: FootprintLayer | null = null; // translucent FILLED parcel floors (Batch C-3, top-down visible)
  let plantLayer: PlantLayer | null = null; // dispatch-lit generator-tower columns (10.6 SceneSpec applier)
  let prosumerLayer: ProsumerLayer | null = null; // battery-bar columns at prosumer parcels (10.6 SceneSpec applier)
  let nodeAssetLayer: NodeAssetLayer | null = null; // recolours node points by lit/outage (caches; skips unchanged)
  // Phase 1.1: voltage overlay (partially wired; the toggle + render path is deferred)
  let nodeVoltageLayer: NodeVoltageLayer | null = null;
  let voltageOverlay = false;
  let handler: ScreenSpaceEventHandlerLike | null = null;
  let unsubscribe: (() => void) | null = null;
  let model: VizModel | null = null; // captured at mount — handed to the effects encoder each render
  const edgeIndex = new Map<string, PolylineLike>();
  const edgeTier = new Map<string, string>(); // edge id → vTier (arrow-material swap lookup)
  // CAPACITY-PROPORTIONAL RENDERING: capture per-edge kV + rateMva at mount so the viz can render
  // edges and arrows proportional to voltage/capacity (not just absolute MW or coarse tier).
  const edgeKv = new Map<string, number>(); // edge id → kV (for fine-grained color within EHV)
  const edgeRating = new Map<string, number>(); // edge id → rateMva (for capacity-proportional width/arrow)

  // CAPACITY-AWARE COLOR: differentiate 500kV/230kV/115kV within EHV (the coarse TIER_COLOR lumps them).
  // Returns a hex color that varies continuously with kV — high kV = bold/dark, low kV = lighter.
  const capacityColor = (tier: string, kv: number | undefined): string => {
    if (tier === "EHV") {
      if (kv === undefined) return TIER_COLOR["EHV"]!;
      if (kv >= 400) return "#1a0a5e"; // 500kV — deep indigo (the backbone, visually dominant)
      if (kv >= 300) return "#2c1a8a"; // 345kV — dark purple
      if (kv >= 200) return "#332288"; // 230kV — blue-purple (the original EHV color)
      return "#4a3a9e"; // 115kV — lighter blue-purple (distinguishable from 230kV+)
    }
    return TIER_COLOR[tier] ?? TIER_COLOR["MV"]!;
  };
  // P10/#136: the per-edge material state machine — owns the F/R/IDLE/OFF decision, the skip-guard, and
  // the transformer-leg set (windings render SOLID). The adapter keeps the GPU material maps + the lookup.
  const flowState = new EdgeFlowStateMachine();
  const nodeIndex = new Map<string, PointPrimitiveLike>(); // keyed by raw node id (matches AssetSpec.id)
  let selOutlineLines: PolylineCollectionLike | null = null; // the outline's own polyline collection (raw, adapter-owned)
  // P10/#136: the selection subsystem — owns the ring marker + parcel-border outline + the edge/parcel anchor
  // maps + the lastSel diff. The adapter keeps the two raw collections (markerPoints/selOutlineLines) + their
  // create/add/lift/dispose; this owns the LOGIC.
  const selection = new SelectionController();
  // Mirrors state/index.ts's seed. compose.ts only calls setLayerVisible() on a CHANGE from the store's
  // initial value, so if this default disagreed with the store's initial value the mismatch would never
  // be corrected (no diff ⇒ no sync call) — finance starts OFF here to match the store's default.
  const layersVisible: Record<Layer, boolean> = { energy: true, information: true, finance: false };
  let quality: QualityTier = "high";
  let lastRenderScale = 1; // drawing-buffer px per CSS px set by the last setQuality (for the perf HUD)
  let dprCleanup: (() => void) | null = null; // tears down the devicePixelRatio-change watcher
  let basemap: BasemapKind = "offline";
  let tileset3d: Cesium3DTilesetLike | null = null; // Google Photorealistic 3D Tiles (Batch C-1), cached
  // float-above handles (Batch C): the inline-built static primitives + the grid centroid, so the lift
  // can be applied/cleared uniformly across EVERY grid container on a 3D↔2D basemap switch.
  let minorLines: PolylineCollectionLike | null = null; // pruned minor wiring (display extra)
  let cpPoints: PointPrimitiveCollectionLike | null = null; // connection-point markers (display extra)
  let facilityParcelLayer: FacilityParcelLayer | null = null; // P14: facility wall+fill, recoloured by node condition each tick
  let gridCentroid: { lon: number; lat: number } | null = null; // bbox centre — the lift's up-vector anchor
  let liftActive = false; // is the grid currently floated (a 3D basemap is showing)?
  let displayYear = 2026; // calendar year the date UI shows / the sun uses (cosmetic — angle is year-free)
  let lastSunDate: SunDate | null = null; // last (season,hour,year) → calendar pushed to the Cesium clock
  let lastSnapshot: Snapshot | null = null;
  const pickCbs: ((sel: PickSel) => void)[] = [];

  // Batch C-2 — drive the Cesium sun from the sim's (season, hour). Writing clock.currentTime moves the
  // SunLight angle; enableLighting (set in mount) makes it visible on the globe. The sim owns time, so
  // Cesium never self-advances (shouldAnimate=false). Skips a redundant write when the instant is unchanged.
  function applySun(snap: Snapshot): void {
    if (!viewer) return;
    const sd = sunDate(snap.scenario.season, snap.scenario.hour, displayYear);
    if (lastSunDate && lastSunDate.utcYear === sd.utcYear && lastSunDate.utcMonth === sd.utcMonth
      && lastSunDate.utcDay === sd.utcDay && lastSunDate.utcHour === sd.utcHour) return;
    lastSunDate = sd;
    const greg = new cesium.GregorianDate(sd.utcYear, sd.utcMonth, sd.utcDay, sd.utcHour, 0, 0);
    viewer.clock.currentTime = cesium.JulianDate.fromGregorianDate(greg);
  }

  // Batch C-1 — Google Photorealistic 3D Tiles. DIRECT Google key (NO ion, R1-safe). Async: the factory
  // fetches the tileset root over the network (the lone 3D network path, same opt-in as osm/satellite).
  // No key, or a load failure ⇒ graceful fall back to satellite imagery — NEVER throw into the render
  // loop, and NEVER fall back to ion / OSM-buildings (both ion-only). Credentials are user-supplied.
  async function enable3DTileset(key: string | undefined): Promise<void> {
    if (!viewer) return;
    if (!key) { fallbackTo2D("satellite"); return; }
    if (tileset3d) { tileset3d.show = true; viewer.scene.globe.show = false; viewer.scene.globe.depthTestAgainstTerrain = true; applyLift(true); return; } // cached — just re-show
    try {
      const ts = await cesium.createGooglePhotorealistic3DTileset({ key, onlyUsingWithGoogleGeocoder: true });
      if (!viewer) return; // disposed mid-await
      tileset3d = ts;
      viewer.scene.primitives.add(ts);
      viewer.scene.globe.show = false; // the 3D tiles carry their own terrain+imagery — hide the ellipsoid
      viewer.scene.globe.depthTestAgainstTerrain = true; // Phase 2.6: tileset depth is the authoritative surface
      applyLift(true); // float the whole grid above the city so it's never occluded by the 3D buildings
      viewer.scene.requestRender?.();
    } catch {
      fallbackTo2D("satellite"); // network/key failure — degrade, don't break the scene
    }
  }
  function remove3DTileset(): void {
    if (tileset3d && viewer) viewer.scene.primitives.remove(tileset3d);
    tileset3d = null;
  }

  // The fidelity knob (shared by the public setQuality + the dpr watcher). RESOLUTION is the blur fix +
  // true 4K: Cesium's default useBrowserRecommendedResolution=true renders at CSS px and IGNORES
  // devicePixelRatio → soft/half-res on HiDPI & 4K. We pick a target renderScale (drawing-buffer px per
  // CSS px), flip the flag FALSE (so Cesium multiplies by dpr) and set resolutionScale = renderScale/dpr,
  // giving a net buffer of CSS×dpr×(renderScale/dpr) = CSS×renderScale exactly, independent of dpr.
  function applyQuality(tier: QualityTier): void {
    quality = tier; // fidelity only — geometry/selection/snapshot untouched
    // Read the FULL knob set from the central table (DRY with the effects layer). v1.1.1: this replaces
    // the inline per-tier ternaries so the v2 governor's six tiers all resolve without a code change here.
    const knobs = tierKnobs(tier);
    // Guard against a teardown / basemap-switch race: only touch a LIVE scene, and never let a transient
    // pipeline-reconfigure error escape — Cesium's render loop halts the whole app on an uncaught throw
    // ("Rendering has stopped"), so a fidelity tweak must degrade silently, never crash rendering.
    if (viewer && viewer.isDestroyed?.() !== true) {
      const scene = viewer.scene;
      const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
      const renderScale = knobs.renderScale(dpr); // per-tier: true native / crisp / 1 CSS px / downscaled
      lastRenderScale = renderScale;
      try {
        // resolutionScale is the DOMINANT, fully-safe adaptive lever + the blur fix (see header). It only
        // resizes the drawing buffer — the most exercised Cesium path — so the governor leans on it.
        viewer.useBrowserRecommendedResolution = false;
        viewer.resolutionScale = renderScale / dpr;
        // EXCELLENT EFFECTS, applied conservatively. Change MSAA / FXAA / fog ONLY when the value actually
        // differs, so a stable tier never churns the framebuffer. HDR is deliberately NOT toggled: float
        // HDR-framebuffer recreation is Cesium's most driver-fragile path (a "this object was destroyed"
        // render-loop crash on some GPUs) and adds little to this emissive line/wall scene — MSAA + native
        // resolution + FXAA + finer 3D tiles carry the quality safely. (HDR seam members stay, unused.)
        const msaa = knobs.msaaSamples;
        if (typeof scene.msaaSamples === "number" && scene.msaaSamples !== msaa) scene.msaaSamples = msaa;
        const fxaaOn = knobs.fxaa;
        if (scene.postProcessStages && scene.postProcessStages.fxaa.enabled !== fxaaOn) {
          scene.postProcessStages.fxaa.enabled = fxaaOn;
        }
        // Phase 2.5 bloom — REMOVED (2026-07-06 pt.2). The brightness/contrast retune (brightness:-0.55,
        // contrast:32) that replaced Cesium's raw Sandcastle-demo defaults was NOT sufficient: the user
        // reported the flashing/oscillating-brightness bug persisted. Root cause was architectural, not
        // numerical — bloom is a full-screen pass that reacts every rendered frame to whatever is
        // currently emissive, and TWO live processes keep changing that: (1) the quality-tier governor
        // (src/app/governor.ts) toggles bloom on/off whenever it steps across the high/mediumplus tier
        // boundary, and its probe-up logic aborts on a SINGLE bad frame (near-zero hysteresis on that
        // side) — on any device that sits near that FPS boundary this flips bloom on and off every few
        // seconds; (2) the P14 loading-danger material bands (flow-material.ts bandGlow) legitimately
        // change per-edge emission by ~10x tick-to-tick as dispatch/loading shifts, which bloom (at ANY
        // brightness/contrast tuning) cannot distinguish from noise. Per explicit user authorization,
        // bloom is now force-disabled UNCONDITIONALLY here — never enabled at any tier, never reacting
        // to any per-tick content — rather than re-tuned a second time. This assignment is defense-in-
        // depth even though effects/quality.ts's TIER_KNOBS also no longer sets bloom:true anywhere: a
        // single unconditional write here means bloom can never again be silently re-enabled by a future
        // tier-table edit. See adapter.test.ts (bloom-removed regression test) + build log.
        const bloom = scene.postProcessStages?.bloom;
        if (bloom && bloom.enabled !== false) {
          bloom.enabled = false;
        }
        const fogOn = knobs.fog;
        if (scene.fog && scene.fog.enabled !== fogOn) scene.fog.enabled = fogOn;
        if (tileset3d) {
          tileset3d.maximumScreenSpaceError = knobs.tileSse;
        }
      } catch {
        /* transient pipeline-reconfigure race (teardown / basemap switch) — degrade, never halt the loop */
      }
    }
    // Phase 0.3: wallsOutline — the LOW tier hides parcel walls/footprints (the OIT cost is
    // significant on weak GPUs). Every other tier keeps them visible. This replaces the prior
    // hardcoded `setShow(true)` that ignored the flag entirely.
    const wallsOn = !knobs.wallsOutline;
    wallLayer?.setShow(wallsOn);
    footprintLayer?.setShow(wallsOn);
    // Phase 0.2: apply the live glyph cap to every glyph layer. The pool was sized at mount to the
    // max across tiers (4000/400/200); the live cap drops with quality so a weaker device renders fewer
    // marching arrows without rebuilding the layer. Infinity (ultra/highplus/high) ⇒ the pool ceiling.
    const gc = knobs.glyphCap;
    energyGlyphs?.setGlyphCap(gc);
    infoGlyphs?.setGlyphCap(gc);
    for (const leg of financeLegs) leg.glyphs.setGlyphCap(gc);
    // Phase 0.3: flowCadence — scales the arrow march speed (1 = full, 4 = quarter). The low tier
    // slows the train so fewer intermediate position writes are needed to read smoothly.
    energyGlyphs?.setCadence(knobs.flowCadence);
    infoGlyphs?.setCadence(knobs.flowCadence);
    for (const leg of financeLegs) leg.glyphs.setCadence(knobs.flowCadence);
    // Phase 0.3: arcDensityFrac + arcsStatic — scale the overlay-arc pool and (low tier) collapse
    // arcs to a static midpoint dot instead of the full ballistic arch + marching glyph train.
    infoArcs?.setDensity(knobs.arcDensityFrac);
    infoArcs?.setStaticMode(knobs.arcsStatic);
    for (const leg of financeLegs) {
      leg.arcs.setDensity(knobs.arcDensityFrac);
      leg.arcs.setStaticMode(knobs.arcsStatic);
    }
    viewer?.scene.requestRender?.();
  }

  // Keep the render buffer crisp when devicePixelRatio CHANGES after mount — browser zoom (Ctrl +/−), an
  // OS display-scale change, or dragging the window between monitors of different density. resolutionScale
  // is computed from dpr at apply time, but Cesium recomputes the buffer each frame from the LIVE dpr, so
  // a stale scale would drift the buffer off CSS×renderScale (blurry, or over-allocating past the tier's
  // cap). We re-apply the SAME tier on any dpr change (recomputing resolutionScale = renderScale/newDpr).
  // matchMedia('(resolution: Ndppx)') fires when dpr leaves N — re-armed at the new dpr each time; a
  // window 'resize' listener is the belt-and-suspenders fallback. Both go through the dpr-changed guard.
  function watchDpr(): void {
    if (typeof window === "undefined") return;
    let appliedDpr = window.devicePixelRatio || 1;
    let mql: MediaQueryList | null = null;
    const recheck = (): void => {
      const dpr = window.devicePixelRatio || 1;
      if (dpr === appliedDpr) return; // a plain window resize (no dpr change) — Cesium handles the canvas
      appliedDpr = dpr;
      applyQuality(quality); // recompute resolutionScale = renderScale/dpr → buffer stays CSS×renderScale
      arm();
    };
    const arm = (): void => {
      if (typeof window.matchMedia !== "function") return;
      mql?.removeEventListener?.("change", recheck);
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      mql.addEventListener?.("change", recheck);
    };
    window.addEventListener("resize", recheck);
    arm();
    dprCleanup = (): void => {
      window.removeEventListener("resize", recheck);
      mql?.removeEventListener?.("change", recheck);
    };
  }
  // Restore a 2D imagery basemap + the globe (used on switch-away from 3D and on graceful fallback).
  function fallbackTo2D(kind: Exclude<BasemapKind, "photoreal3d">): void {
    if (!viewer) return;
    basemap = kind;
    remove3DTileset();
    applyLift(false); // drop the grid back onto the flat ground
    viewer.scene.globe.show = true;
    viewer.scene.globe.depthTestAgainstTerrain = false; // Phase 2.6: off on flat ellipsoid (z-fight risk)
    viewer.imageryLayers.removeAll();
    viewer.imageryLayers.addImageryProvider(buildImageryProvider(cesium, kind));
    viewer.scene.requestRender?.();
  }

  // Batch C float-above. The modelMatrix that lifts every grid container by GRID_LIFT_3D_M along the
  // geodetic up at the GTA centroid (so the network floats above the 3D city), or identity when off.
  // A pure ECEF translation along the centroid normal is visually exact over the GTA's ~100 km extent.
  function liftMatrix(active: boolean): unknown {
    if (!active || !gridCentroid) return cesium.Matrix4.fromTranslation(new cesium.Cartesian3(0, 0, 0));
    const c = cesium.Cartesian3.fromDegrees(gridCentroid.lon, gridCentroid.lat, 0);
    const up = cesium.Cartesian3.normalize(c, new cesium.Cartesian3());
    const t = cesium.Cartesian3.multiplyByScalar(up, GRID_LIFT_3D_M, new cesium.Cartesian3());
    return cesium.Matrix4.fromTranslation(t);
  }
  // Apply (active=true, over 3D tiles) or clear (active=false, flat basemap) the lift on EVERY grid
  // container — collections the adapter owns directly + the layer-owned primitives (via setModelMatrix)
  // + the inline static display extras — so the whole grid rises/falls together and stays self-consistent.
  function applyLift(active: boolean): void {
    liftActive = active;
    const m = liftMatrix(active);
    for (const col of edgeCollections.values()) col.modelMatrix = m;
    if (arcLines) arcLines.modelMatrix = m;
    if (minorLines) minorLines.modelMatrix = m;
    if (nodePoints) nodePoints.modelMatrix = m;
    if (markerPoints) markerPoints.modelMatrix = m;
    if (cpPoints) cpPoints.modelMatrix = m;
    if (energyGlyphs) {
      energyGlyphs.collection.modelMatrix = m;
      if (energyGlyphs.haloCollection) energyGlyphs.haloCollection.modelMatrix = m; // the danger-halo pool lifts too
    }
    if (infoGlyphs) infoGlyphs.collection.modelMatrix = m;
    for (const leg of financeLegs) leg.glyphs.collection.modelMatrix = m;
    wallLayer?.setModelMatrix(m);
    footprintLayer?.setModelMatrix(m);
    plantLayer?.setModelMatrix(m);
    prosumerLayer?.setModelMatrix(m);
    facilityParcelLayer?.setModelMatrix(m);
    viewer?.scene.requestRender?.();
  }

  // per-edge animated-flow intensity = Σ (over visible layers) of magnitude·direction, capped by quality.
  function applyFlows(state: UiState): void {
    for (const line of edgeIndex.values()) line.flowMagnitude = 0;
    let budget = tierKnobs(quality).flowCap;
    for (const layer of LAYERS) {
      if (!layersVisible[layer]) continue;
      for (const f of selFlows(layer)(state)) {
        if (budget <= 0) break;
        const line = edgeIndex.get(f.edgeId as string);
        if (!line) continue;
        line.flowMagnitude = (line.flowMagnitude ?? 0) + f.magnitude * f.direction;
        budget--;
      }
    }
    // (the redundant flow-dot particle layer was removed in the engine-v2 flow redesign — the
    // marching-chevron edge material now carries direction + energization, the single energy-flow display.)
    // info/finance samples ride synthetic `from→to` ids the edge index can never match — they draw
    // as elevated arcs from a fixed pool (the R11 per-layer flow viz for the two overlay layers).
    if (infoArcs) {
      if (layersVisible.information) infoArcs.apply(selFlows("information")(state), posOf);
      else infoArcs.hideAll();
    }
    // marching arrow-glyphs along the info/finance arcs (restyled per user: distinct colour/path/size,
    // marching portion-by-portion). Geometry is dynamic (the arcs change each tick), so rebuild the
    // bounded track set from the active samples, then mark them all active.
    overlayGlyphs(infoGlyphs, layersVisible.information ? selFlows("information")(state) : [], INFO_ARC.apexScale, INFO_ARC.arrow);
    // finance splits into 3 settlement legs by FlowSample.kind — each its own coloured/arched arc + glyphs
    // so commodity→generator, transmission→transmitter, distribution→LDC read as distinct money flows.
    const finFlows = layersVisible.finance ? selFlows("finance")(state) : [];
    for (const leg of financeLegs) {
      const legFlows = finFlows.filter((f) => leg.cfg.match(f.kind));
      if (layersVisible.finance) leg.arcs.apply(legFlows, posOf);
      else leg.arcs.hideAll();
      overlayGlyphs(leg.glyphs, legFlows, leg.cfg.apexScale, leg.cfg.arrow);
    }
  }

  // Feed an overlay glyph layer from synthetic from→to FlowSamples: build a BALLISTIC ARC track per
  // flow (same sine arch the overlay-arc LINE draws, so the arrows ride the line) and mark it active.
  // The arrow `colorCss` is the CONTRASTING transaction colour (≠ the bright-blue line) per the user.
  function overlayGlyphs(
    layer: FlowGlyphLayer | null, flows: readonly FlowSample[], apexScale: number, arrowColorCss: string,
  ): void {
    if (!layer) return;
    const edges: GlyphEdge[] = [];
    const active = new Map<string, GlyphActive>();
    const cap = 120; // bounded display budget (matches the arc pools' spirit)
    const stride = Math.max(1, Math.ceil(flows.length / cap));
    for (let i = 0; i < flows.length && edges.length < cap; i += stride) {
      const f = flows[i]!;
      const ep = arcEndpoints(f.edgeId as string);
      if (!ep) continue;
      const a = posOf(ep.from);
      const b = posOf(ep.to);
      if (!a || !b) continue;
      const id = f.edgeId as string;
      const arc = ballisticArc(a, b, { apexScale }); // arrows follow the arch (per-vertex heights)
      edges.push({ id, polyline: arc.lonlat, heights: arc.heights });
      active.set(id, {
        colorCss: arrowColorCss, flowDir: f.direction,
        flowMag: Math.min(1, Math.abs(f.magnitude) / 50), mw: Math.abs(f.magnitude),
      });
    }
    layer.setEdges(edges);
    layer.setActive(active);
  }

  // P14 loading-danger band per edge: prefer the encoded `spec.crisis` (the dead channel P14 wires live —
  // captured each render in `crisisBand`), fall back to recomputing from `loadingPct` when no effects
  // encoder is injected (so the ramp still works in bare/test wiring). bucket==encodeCrisis by construction.
  let crisisBand: ReadonlyMap<string, CrisisBucket> | null = null;
  const edgeBand = (id: string, loadingPct: number): CrisisBucket => crisisBand?.get(id) ?? bandOf(loadingPct);

  // Build the energy-edge glyph active-set from the engine's per-branch flow contract: a marching
  // arrow track ONLY where the edge is energized AND carrying power (flowDir≠0). Direction + magnitude
  // are the engine's — prosumer export / FLISR re-routing flip the arrows for free. A hot/red edge also
  // gets a danger HALO behind its arrows (P14, the third orthogonal channel: tier body · energy size · danger halo).
  //
  // Phase 1.5 (engine.v3 P-viz): flowMag/flowDir now ride the wire for real (protocol.ts's combined
  // 1.3+1.5 bump) — a REAL production snapshot always has them defined (the packer defaults absent
  // engine values to 0, never `undefined`). The `??` fallbacks below are NOT the production path
  // anymore; they're a last-resort default for a Snapshot built BY HAND (most of adapter.test.ts's
  // synthetic fixtures deliberately omit flowMag/flowDir to exercise this exact fallback) or an older
  // in-process caller that never went through the wire. Kept deliberately (removing them would just
  // turn "hand-built snapshot omits a field" into a hard crash instead of a graceful recompute), but the
  // REAL bug (arrows drawn from a client-recomputed magnitude that silently diverged from the engine's
  // DLR-derated rating) is fixed: production traffic never reaches these fallbacks anymore.
  // Phase 1.3 (engine.v3 P-viz): ISLAND-membership glyph tint — visible ONLY once a fault has actually
  // split the grid into more than one live (non-negative islandId) island; a single-island steady state
  // renders exactly as before (tier colour only), so this never recolours the normal, healthy map. Tints
  // the GLYPH (a simple per-instance billboard colour, no shader involved) rather than the edge LINE
  // material — the line's Fabric materials are still cached per (tier,band,dir) (Phase 2.1's shader-
  // permutation collapse hasn't landed yet), and adding a 4th ISLAND dimension there would multiply the
  // very permutation count Phase 2.1 exists to shrink. The marching arrows already carry the tier +
  // loading-danger channels; island hue rides along as a THIRD blended cue, consistent with this app's
  // redundant-channel design (colour is never the only signal).
  const ISLAND_MIX = 0.55; // prominent enough to read as "this is a different island", tier still visible
  function energyGlyphActive(snap: Snapshot): Map<string, GlyphActive> {
    const active = new Map<string, GlyphActive>();
    if (!layersVisible.energy) return active;
    const islands = new Set<number>();
    for (const bf of snap.branchFlows) if (bf.islandId !== undefined && bf.islandId >= 0) islands.add(bf.islandId);
    const multiIsland = islands.size > 1;
    for (const bf of snap.branchFlows) {
      if (!bf.energized) continue;
      if (flowState.isTransformerLeg(bf.edgeId as string)) continue; // V4 P-FIX: a transformer winding gets NO marching glyph
      const dir: 1 | -1 | 0 = bf.flowDir ?? (bf.flowMw > 0 ? 1 : bf.flowMw < 0 ? -1 : 0);
      if (dir === 0) continue;
      const id = bf.edgeId as string;
      const tier = edgeTier.get(id) ?? "MV";
      const kv = edgeKv.get(id);
      const mag = bf.flowMag ?? Math.min(1, bf.loadingPct / 100);
      const band = edgeBand(id, bf.loadingPct);
      // CAPACITY-AWARE COLOR: differentiate 500kV/230kV/115kV (the coarse TIER_COLOR lumps them into EHV)
      const base = capacityColor(tier, kv);
      const isle = multiIsland && bf.islandId !== undefined ? islandColor(bf.islandId) : null;
      active.set(id, {
        colorCss: isle ? mixHex(base, isle, ISLAND_MIX) : base, flowDir: dir, flowMag: mag, mw: Math.abs(bf.flowMw ?? 0),
        ...(band === "hot" || band === "red" ? { halo: band } : {}),
      });
    }
    return active;
  }

  // P14 selection width — a fixed bumped width (above the 4.5px loading max), quantize-safe (a constant
  // never churns the width setter) so the selected edge reads as the thickest line on the map.
  const SEL_WIDTH = 6;

  // P14: lazily build + cache the band-tinted chevron / idle materials. The base-band variants are built
  // eagerly at mount (so the steady fixture keeps the base material count); warn/hot/red are created here
  // on first use when an edge actually crosses a loading band — a bounded matrix (tiers × 4 bands × dir).
  function flowMatFor(tier: string, band: CrisisBucket, dir: "F" | "R"): MaterialLike | undefined {
    const key = `${tier}:${band}:${dir}`;
    let m = flowMaterials.get(key);
    if (!m) { m = makeFlowMaterial(cesium, tier, dir === "F" ? 1 : -1, band); flowMaterials.set(key, m); }
    return m;
  }
  function idleMatFor(tier: string, band: CrisisBucket): MaterialLike | undefined {
    const key = `${tier}:${band}`;
    let m = idleMaterials.get(key);
    if (!m) { m = makeInServiceMaterial(cesium, tier, band); idleMaterials.set(key, m); }
    return m;
  }

  function render(state: UiState): void {
    const snap = state.latest;
    if (!snap || !flowMaterial) return;
    lastSnapshot = snap;
    applySun(snap); // Batch C-2: sun angle follows the sim's season + hour (no-op if unchanged)
    // P14: compute the SceneSpec ONCE up front so its crisis channel drives the edge loading-danger ramp
    // (the dead `spec.crisis` is now consumed) and its asset condition drives the facility-parcel recolour.
    let spec: SceneSpec | undefined;
    if (effects && model) {
      const out = effects.encode({
        snapshot: snap, prosumers: selProsumers(state), loads: selLoads(state),
        model, quality, layersVisible,
      });
      spec = out ?? undefined;
    }
    crisisBand = spec ? new Map<string, CrisisBucket>(spec.crisis.map((c) => [c.edgeId, c.bucket])) : null;
    // P14 edge SELECTION: the one clicked cable rides a bright highlight. Detect a change BEFORE the engine
    // edge loop and invalidate the previously-selected edge so the loop RESTORES its proper material this
    // same render; the highlight on the now-selected edge is (re-)asserted AFTER the loop (so it wins even
    // if that edge's band/state changed this tick).
    const selRaw = state.selection;
    const selEdge = selRaw && selRaw.kind === "edge" && edgeIndex.has(String(selRaw.id)) ? String(selRaw.id) : null;
    if (selEdge !== selectedEdgeId && selectedEdgeId) flowState.invalidate(selectedEdgeId);
    for (const bf of snap.branchFlows) {
      const id = bf.edgeId as string;
      const line = edgeIndex.get(id);
      if (!line) continue;
      if (id === selEdge) continue; // the selected edge is styled by the highlight block below — skip the engine restyle (no per-render width/material churn)
      // CAPACITY-PROPORTIONAL width: base width from rateMva (backbone = thick, feeder = thin), modulated
      // by loading pressure (an overloaded line grows wider). Quantized to 0.25 steps for the Cesium
      // re-batch guard. A 500kV backbone (rateMva ~4000+) starts at ~4px; a 27kV feeder (rateMva ~50)
      // starts at ~1.5px. Loading pressure adds up to +2px on top.
      const capW = Math.max(1, Math.min(4, 1 + Math.min(1, Math.sqrt((edgeRating.get(id) ?? 0) / 4000)) * 3));
      line.width = Math.round((capW + Math.min(bf.loadingPct, 100) / 100 * 1.5) * 4) / 4;
      // ENGINE-DRIVEN material, THREE states × loading BAND (the state:band key gates the swap so a steady
      // edge never re-batches; a band crossing recolours to the danger-ramp variant of the same chevron):
      //   "F"/"R" — energized AND carrying flow ⇒ marching chevrons (direction = sign(flowMw), hue by band);
      //   "IDLE"  — IN SERVICE but ~0 flow this tick ⇒ SOLID tier/band line, no arrows (a live circuit);
      //   "OFF"   — de-energized / out of service ⇒ dead GREY line (an outage reads dead regardless of tier).
      // Energy layer toggled off ⇒ everything grey, like a cold grid.
      const band = edgeBand(id, bf.loadingPct);
      const wantState = flowState.wantedIfChanged(bf, layersVisible.energy, band);
      if (wantState !== null) {
        const tier = edgeTier.get(id) ?? "MV";
        const mat = wantState === "OFF"
          ? staticMaterials.get(tier)
          : wantState === "IDLE"
            ? idleMatFor(tier, band)
            : flowMatFor(tier, band, wantState);
        if (mat) {
          line.material = mat;
          flowState.commit(id, wantState, band); // commit only on a found material (byte-faithful: retry if missing)
        }
      }
    }
    // (re-)assert the selection highlight on the currently-selected edge — its whole length recolours +
    // bumps width. selectionMat is built LAZILY on the first edge select (so mount's material count is stable).
    if (selEdge) {
      const line = edgeIndex.get(selEdge);
      if (line) {
        if (!selectionMat) selectionMat = makeSelectionMaterial(cesium);
        line.material = selectionMat;
        line.width = SEL_WIDTH;
      }
    }
    selectedEdgeId = selEdge;
    // engine-driven marching ARROW-GLYPHS: assign big arrow sprites to the energized, power-carrying
    // edges (the rAF clock in mount marches them) + a danger HALO on hot/red edges. De-energized/idle
    // edges get none — the static polyline alone remains, so a failure makes its downstream arrows vanish.
    energyGlyphs?.setActive(energyGlyphActive(snap));
    applyFlows(state);
    // (flow time is owned by the rAF clock in mount — a per-snapshot integer write would freeze it)
    if (spec) {
      // translucent FILLED parcel floors FIRST (added to the scene before the wall band ⇒ the band/points
      // stay on top for clicks, the floor is still drill-pickable for a top-down click). Same SceneSpec.walls.
      if (footprintLayer) footprintLayer.apply(spec.walls); // top-down-visible parcel fills (Batch C-3)
      if (wallLayer) wallLayer.apply(spec.walls); // glowing parcel walls (geometry built once)
      // capture neighbourhood parcel rings once (static geometry) for the selection border highlight
      if (spec.walls.length > 0) selection.seedWallRings(spec.walls); // capture parcel rings once
      if (plantLayer) plantLayer.apply(spec.plants); // dispatch-lit plant towers (geometry built once)
      if (prosumerLayer) prosumerLayer.apply(spec.prosumers); // battery bars (geometry built once)
      // Phase 1.1: when the voltage overlay is ON, recolour node points by live busVMagPu (a green/
      // yellow/orange/red ramp by per-unit voltage); when OFF, the default outage/status coloring runs.
      if (voltageOverlay && nodeVoltageLayer) nodeVoltageLayer.apply(snap.busVMagPu);
      else if (nodeAssetLayer) nodeAssetLayer.apply(spec.assets);
      // P14: recolour facility parcels (substation/plant/switch footprints) by their node's service
      // CONDITION (normal identity hue → stressed/shortage/outage danger hue), mutated in place.
      if (facilityParcelLayer) {
        const statusByNode = new Map<string, AssetStatus>();
        for (const a of spec.assets) if (a.status) statusByNode.set(a.id, a.status);
        facilityParcelLayer.apply((nodeId) => statusByNode.get(nodeId) ?? "normal");
      }
    }
    viewer?.scene.requestRender?.();
  }

  return {
    mount(container, mountModel: VizModel): void {
      model = mountModel;
      viewer = new cesium.Viewer(container, {
        baseLayer: false, // no default ion imagery
        baseLayerPicker: false,
        geocoder: false,
        timeline: false,
        animation: false,
        terrainProvider: new cesium.EllipsoidTerrainProvider(),
        // P16: render ON DEMAND, not an unconditional 60 fps. With this OFF, Cesium redrew ~2771 polylines
        // 60×/s on a 1 Hz sim and the ~12 scene.requestRender() calls were no-ops. Now a render happens only
        // when something asks (the gated rAF below while arrows animate, camera moves, fresh snapshot,
        // selection/quality change) — an idle scene (paused/blacked-out/all-layers-off, still camera) idles.
        requestRenderMode: true,
        maximumRenderTimeChange: Infinity, // the SIM drives clock.currentTime; never auto-render on clock drift
      });
      viewer.imageryLayers.addImageryProvider(buildImageryProvider(cesium, basemap));
      // Batch C-2: light the globe with the sun (default is OFF — the "sun does nothing" trap) and keep
      // Cesium's clock frozen so it never self-advances — the SIM drives clock.currentTime each render
      // (applySun), so the sunlight angle stays synced to the simulator's (season, hour), not wall time.
      viewer.scene.globe.enableLighting = true;
      viewer.clock.shouldAnimate = false;
      watchDpr(); // re-apply the active tier on any devicePixelRatio change (zoom / monitor move) — keeps crisp

      // one collection per voltage tier PRESENT in the model + the BASE-band tier-tinted materials per
      // tier (F/R marching chevron + IDLE solid + OFF dead-grey). render() picks per edge from the engine's
      // `energized` flag + flow sign, and swaps to a warn/hot/red BAND variant (built lazily) as loading
      // rises. Building only the base band here keeps the mounted material count tier×4 (+arcs).
      const tierCollection = (tier: string): PolylineCollectionLike => {
        let col = edgeCollections.get(tier);
        if (!col) {
          col = new cesium.PolylineCollection();
          edgeCollections.set(tier, col);
          flowMaterials.set(`${tier}:base:F`, makeFlowMaterial(cesium, tier, 1, "base"));
          flowMaterials.set(`${tier}:base:R`, makeFlowMaterial(cesium, tier, -1, "base"));
          staticMaterials.set(tier, makeStaticMaterial(cesium, tier)); // dead grey (tier-independent hue)
          idleMaterials.set(`${tier}:base`, makeInServiceMaterial(cesium, tier, "base"));
          if (edgeLines === null) {
            edgeLines = col;
            flowMaterial = flowMaterials.get(`${tier}:base:F`)!; // legacy single refs = the FIRST tier's base F
          }
        }
        return col;
      };
      for (const e of model.edges) {
        // V3-swap REV-A: a superseded legacy CORE transmission line (V3 backbone represents the corridor) is
        // NOT drawn — the default map shows only the V3 network. It stays in the model/data (engine opens it;
        // kept for the gate), just hidden from view so the old lines are cleared from the map.
        if (e.supersededLegacy) continue;
        // build log 87 (user: "the edges between neighbourhoods and substations are not visible, the flow arrows
        // move on empty air"): the intra-GTA DEMAND network — the Debug-3 distribution feeders (neighbourhood
        // <-> substation) + neighbour ties — is the LIVE load-delivery + self-heal wiring (V3 is a transmission/
        // generation geograph with NO distribution), so it MUST be drawn. Hiding the E_/CR_ feeders left every
        // load disconnected on the map and the energy arrows riding invisible edges. We now draw the whole demand
        // mesh; only a normally-open BACKUP feeder stays hidden until FLISR closes it (drawing it would double
        // every feed). Superseded edges are already skipped above (line 547).
        if (e.kind === "distribution_feeder" && e.isTie && e.normallyOpen === true) continue;
        const positions = cesium.Cartesian3.fromDegreesArray(edgeDegreesArray(e));
        tierCollection(e.vTier);
        // edges START on the static (off) material — nothing animates until the engine energizes it
        const line = tierCollection(e.vTier).add({
          positions, width: 1, material: staticMaterials.get(e.vTier),
          id: { kind: "edge", id: e.id as string },
        });
        edgeIndex.set(e.id as string, line);
        edgeTier.set(e.id as string, e.vTier);
        // CAPACITY-PROPORTIONAL: capture kV + rateMva for fine-grained color + width + arrow sizing
        edgeKv.set(e.id as string, e.kv ?? 0);
        edgeRating.set(e.id as string, e.rateMva ?? 0);
        // Set initial width proportional to capacity: high-rateMva edges are THICKER (the backbone reads
        // dominant). Quantized to 0.25 steps for the Cesium re-batch guard. rangeMva 25–16800 → width 1–5px.
        const capWidth = Math.max(1, Math.min(5, Math.round((1 + Math.min(1, Math.sqrt((e.rateMva ?? 0) / 4000)) * 4) * 4) / 4));
        line.width = capWidth;
        flowState.register(e.id as string, !!e.isTransformerLeg); // seed OFF + flag transformer windings (no marching arrow)
        const verts = positions as unknown as readonly unknown[];
        selection.registerEdgeMid(e.id as string, verts[Math.floor(verts.length / 2)]); // selection-ring anchor
      }
      // selection ring gets its OWN point collection (the flow-dot particle layer was deleted in the
      // engine-v2 flow redesign). Node anchor = its true coord; edge anchor = the polyline's mid vertex.
      markerPoints = new cesium.PointPrimitiveCollection(); // raw selection-ring pool (SelectionController writes it)

      // overlay-arc pool collection (info/finance) + every node's coord for arc endpoint lookup
      // (junctions included — comms arcs may land on any retained bus, rendered as a point or not).
      arcLines = new cesium.PolylineCollection();
      for (const n of model.nodes) nodePos.set(n.id as string, nodeLonLat(n));
      infoArcs = makeOverlayArcLayer(cesium, arcLines, { colorCss: INFO_ARC.line, apexScale: INFO_ARC.apexScale, pool: 80 }); // bright cyan-blue ballistic arc
      // one glowing arc + "$"-badged glyph layer per settlement leg (commodity/transmission/distribution)
      const arcPool = arcLines; // captured non-null (assigned just above) — closures lose the narrowing
      financeLegs = FINANCE_LEGS.map((cfg) => ({
        cfg,
        arcs: makeOverlayArcLayer(cesium, arcPool, { colorCss: cfg.line, apexScale: cfg.apexScale, pool: 40 }),
        glyphs: makeFlowGlyphLayer(cesium, { pxSize: 16, minPx: 7, refMw: 300, spacingM: 2500, perEdgeCap: 3, maxGlyphs: 200, badge: "$" }),
      }));

      // marching arrow-glyph layers. Energy: big arrows on the true OSM edge geometry (static tracks,
      // built once). Info/finance: smaller arrows along the elevated arcs (dynamic tracks, rebuilt per
      // snapshot in applyFlows). Colour is per-glyph (energy=tier hue; info=cyan, finance=gold here).
      // size scales with |flowMw| via the perceptual sqrt law. P14c: refMw raised 800→1800 + pxSize 20→26 so
      // the dynamic range SPREADS — the 2500–3200 MW nuclear/import backbone now renders near the 26 px max
      // while an 800 MW corridor is clearly mid-size and a tens-of-MW feeder stays small (at refMw 800 every
      // corridor ≥800 MW saturated to ONE size, hiding the "arrow size = energy" channel the user asked for).
      // halo:true builds the P14 danger-halo pool (a soft danger-tinted disc under the arrows on hot/red
      // edges) — the energy layer is the ONLY one with a loading band, so info/finance leave it off.
      energyGlyphs = makeFlowGlyphLayer(cesium, { pxSize: 26, minPx: 6, refMw: 1800, spacingM: 3500, perEdgeCap: 10, maxGlyphs: 4000, halo: true });
      for (const e of model.edges) {
        // use the sibling-fanned render polyline so the marching glyphs ride each parallel circuit's
        // own line (not the shared OSM centerline) — keeps arrows aligned with the fanned polylines.
        edgePolyById.set(e.id as string, edgeRenderPolyline(e));
      }
      energyGlyphs.setEdges(model.edges.map((e) => ({ id: e.id as string, polyline: edgePolyById.get(e.id as string)! })));
      // info/finance arrows carry a BADGE ("i" / "$") baked behind the chevron + a bigger px size so the
      // badge reads — the three parallel flows are now distinct by SHAPE + colour + arc height.
      infoGlyphs = makeFlowGlyphLayer(cesium, { pxSize: 18, minPx: 8, refMw: 50, spacingM: 2500, perEdgeCap: 4, maxGlyphs: 400, badge: "i" });

      nodePoints = new cesium.PointPrimitiveCollection();
      for (const n of model.nodes) {
        if (!isRenderableNode(n)) continue; // skip anonymous junction joints (pole/tower clutter) — edge geometry + solver keep them
        const [lon, lat] = nodeLonLat(n);
        const pt = nodePoints.add({
          position: cesium.Cartesian3.fromDegrees(lon, lat),
          id: { kind: "node", id: n.id as string },
          color: cesium.Color.fromCssColorString("#cfd8dc").withAlpha(1), // neutral asset hue until first AssetSpec
        });
        nodeIndex.set(n.id as string, pt); // keyed by raw id so the node-asset layer matches AssetSpec.id
      }
      nodeAssetLayer = makeNodeAssetLayer(cesium, nodeIndex); // built after the index is populated
      nodeVoltageLayer = makeNodeVoltageLayer(cesium, nodeIndex); // Phase 1.1: voltage overlay (same index)

      for (const col of edgeCollections.values()) viewer.scene.primitives.add(col);
      viewer.scene.primitives.add(arcLines);
      // arrow-glyph billboard collections ride ABOVE the lines/arcs (added last ⇒ drawn on top). The P14
      // danger-halo pool is added FIRST so the arrows draw ON TOP of their halos (halo = a ring behind).
      if (energyGlyphs.haloCollection) viewer.scene.primitives.add(energyGlyphs.haloCollection);
      viewer.scene.primitives.add(energyGlyphs.collection);
      viewer.scene.primitives.add(infoGlyphs.collection);
      for (const leg of financeLegs) viewer.scene.primitives.add(leg.glyphs.collection);
      // parcel-BORDER highlight — its own collection, constructed last so it sits ABOVE the parcels (and
      // the recording index stays after the edge + overlay-arc collections). Joins by node id to the rings
      // gathered from facility parcels + neighbourhood walls.
      selOutlineLines = new cesium.PolylineCollection();
      // build the selection marker + outline over the two raw collections (the controller owns the logic).
      selection.init({ cesium, markerPoints: markerPoints!, selOutlineLines, posOf: (id) => nodePos.get(id) });
      viewer.scene.primitives.add(selOutlineLines); // parcel-border highlight (under the marker halo)
      viewer.scene.primitives.add(markerPoints);
      viewer.scene.primitives.add(nodePoints);

      // ── v3 display fidelity: the Debug 3 components the solver model omits but the map shows ──
      if (display?.prunedLines?.length) {
        // the 2,099 real branches the stub-prune removed from the SOLVER — static minor wiring
        minorLines = new cesium.PolylineCollection();
        const minorMat = new cesium.Material({
          fabric: { type: "Color", uniforms: { color: cesium.Color.fromCssColorString("#7d8e9a").withAlpha(0.45) } },
        });
        for (const pl of display.prunedLines) {
          const flat: number[] = [];
          for (const c of pl.polyline) flat.push(c.lon, c.lat);
          minorLines.add({
            positions: cesium.Cartesian3.fromDegreesArray(flat), width: 1, material: minorMat,
            id: { kind: "minor", id: pl.id },
          });
        }
        viewer.scene.primitives.add(minorLines);
      }
      if (display?.connectionPoints?.length) {
        // 550 taps where facilities/plants wire into the lines (Debug 3 connection_points)
        cpPoints = new cesium.PointPrimitiveCollection();
        const cpColor = cesium.Color.fromCssColorString("#eceff1").withAlpha(0.8);
        for (const cp of display.connectionPoints) {
          const pt = cpPoints.add({
            position: cesium.Cartesian3.fromDegrees(cp.coord.lon, cp.coord.lat),
            id: { kind: "cp", id: cp.id },
            color: cpColor,
          });
          (pt as { pixelSize?: number }).pixelSize = 3; // real Cesium prop; tiny tap markers
        }
        viewer.scene.primitives.add(cpPoints);
      }
      if (display?.facilityParcels?.length) {
        // substation/plant parcels as luminous walls + translucent floors (the blueprint's footprints,
        // top-down visible + selectable), now recoloured by node CONDITION each tick (P14, facility-parcels.ts).
        // PARCEL→NODE CROSSWALK (2026-06-29): facility_parcels.json is keyed by STALE pre-V4 ids
        // (SUB_<osm>/PLANT_<osm>), but the V4 model renamed those buses (W<osm>) + split each station into
        // per-voltage winding nodes (_LV###). Without this, clicking a substation footprint selects a
        // phantom id ⇒ EMPTY inspector. The pure resolver maps each parcel to the REAL model node (exact id →
        // OSM-core base bus → nearest station within range); the layer keys its prims/recolour by that node.
        const resolveParcel = makeParcelResolver(
          model.nodes.map((n) => { const [lon, lat] = nodeLonLat(n); return { id: String(n.id), cls: n.cls, lon, lat }; }),
        );
        const parcels: FacilityParcel[] = [];
        for (const fp of display.facilityParcels) {
          if (fp.ring.length < 3) continue;
          const nodeId = resolveParcel(String(fp.id), fp.ring); // the REAL model node this footprint represents
          selection.seedFacilityRing(nodeId, fp.ring); // border-highlight geometry, keyed by the resolved node
          parcels.push({ nodeId, facType: fp.facType, ring: fp.ring });
        }
        facilityParcelLayer = makeFacilityParcelLayer(cesium, viewer.scene, parcels);
      }

      // Smooth animation clock (browser only — node tests have no rAF). The stripe shader needs a
      // FRACTIONAL, continuously advancing time: the old `time = snap.tick` was an INTEGER, and
      // fract(s − int) is identical for every int — the flow animation was mathematically frozen
      // from day one. dt is clamped so a backgrounded tab's multi-second rAF gap can't teleport dots.
      if (typeof requestAnimationFrame === "function") {
        const animate = (nowMs: number): void => {
          if (!flowMaterial) return; // disposed — stop silently
          // P16 render-gate: only march the glyphs + request a render when there ARE live arrows. With
          // requestRenderMode ON, skipping requestRender() lets an idle scene (no energized power-carrying
          // edges, all flow layers off, or a full blackout — and a still camera) drop toward 0 fps instead of
          // burning 60 fps redrawing 2771 static polylines. Fresh snapshots, camera moves, and selection
          // each requestRender on their own path, so the scene never goes stale. The rAF stays scheduled so
          // animation resumes the instant flows return.
          const animating =
            !!energyGlyphs?.isAnimating() || !!infoGlyphs?.isAnimating()
            || financeLegs.some((leg) => leg.glyphs.isAnimating());
          if (animating) {
            const sec = nowMs / 1000;
            // Phase 2.2 RESEARCH VERDICT (2026-07-06, engine.v3 P-viz — see build log): the roadmap's "~213k
            // writes/sec" premise for replacing this with czm_frameNumber assumed a write PER EDGE. It's
            // actually per DISTINCT MATERIAL (`flowMaterials`, keyed `tier:band:dir` — the same lazy cache
            // flowMatFor() builds), measured live at 23-32 entries even under an active multi-island fault
            // (`window.__gta.viz._debug.edgeMaterials()`). 32 JS writes/frame is not a real perf problem —
            // Phase 2.1's premise (Cesium doesn't support a per-polyline uniform override on a PolylineCollection,
            // confirmed against node_modules/cesium's PostProcessStageLibrary/Material source — this matrix
            // of cached materials IS the workaround) means collapsing further would require migrating every
            // animated edge off PolylineCollection+Fabric onto a per-instance-colour Primitive with a CUSTOM
            // animated-chevron shader — a full rendering-architecture rewrite of the app's central "see power
            // flow" feature, for a theoretical (and, measured, much smaller than assumed) gain. Separately,
            // czm_frameNumber only advances on ACTUALLY-RENDERED frames, which under this app's ALREADY-SHIPPED
            // requestRenderMode (P16) are sparse/irregular when idle — swapping to it would make the chevron
            // march at an inconsistent, render-cadence-dependent speed instead of the wall-clock-constant pace
            // this `sec` (performance.now()/1000) gives it now. BOTH 2.1 and 2.2 are DEFERRED: high migration
            // risk to a load-bearing feature, for a problem measurement shows is not real at the app's current
            // scale. Revisit only if a real device profile shows shader-compile stalls or program-switch cost.
            for (const m of flowMaterials.values()) setFlowTime(m, sec); // base-line sheen (subtle)
            // march the big arrow-glyphs along their tracks (the primary, recognizable flow display)
            energyGlyphs?.advance(sec);
            infoGlyphs?.advance(sec);
            for (const leg of financeLegs) leg.glyphs.advance(sec);
            viewer?.scene.requestRender?.();
          }
          animId = requestAnimationFrame(animate);
        };
        animId = requestAnimationFrame(animate);
      }
      footprintLayer = makeFootprintLayer(cesium, viewer.scene); // translucent parcel floors (Batch C-3), built first render
      wallLayer = makeWallLayer(cesium, viewer.scene); // builds its parcel-wall Primitive on first render
      plantLayer = makePlantLayer(cesium, viewer.scene); // builds its plant-column Primitive on first render
      prosumerLayer = makeProsumerLayer(cesium, viewer.scene); // builds its battery-bar Primitive on first render

      // Frame the grid: without this the GTA is a pin-prick on the full globe. Fit the camera to the
      // bounding box of all node coords (small padding so edge geometry isn't clipped at the border).
      if (model.nodes.length > 0) {
        let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
        for (const n of model.nodes) {
          const [lon, lat] = nodeLonLat(n);
          if (lon < west) west = lon;
          if (lon > east) east = lon;
          if (lat < south) south = lat;
          if (lat > north) north = lat;
        }
        const pad = 0.05;
        gridCentroid = { lon: (west + east) / 2, lat: (south + north) / 2 }; // float-above lift anchor
        viewer.camera.flyTo({
          destination: cesium.Rectangle.fromDegrees(west - pad, south - pad, east + pad, north + pad),
          duration: 0,
        });
      }

      handler = new cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((movement) => {
        if (!viewer) return;
        // drill through overlapping primitives: decorative layers (dots/taps/minor wiring/arcs)
        // sit on top of the selectable assets — take the FIRST hit that resolves to a node/edge.
        let sel = null;
        const stack = viewer.scene.drillPick?.(movement.position, 6, 10, 10);
        if (stack) {
          for (const hit of stack) {
            sel = resolvePick(hit);
            if (sel) break;
          }
        } else {
          sel = resolvePick(viewer.scene.pick(movement.position, 10, 10));
        }
        if (!sel) return;
        store.getState().select(sel); // → state ONLY; never the engine/worker (R9)
        for (const cb of pickCbs) cb(sel);
      }, cesium.ScreenSpaceEventType.LEFT_CLICK);

      unsubscribe = store.subscribe((s) => {
        // selection ring BEFORE render(): render early-returns until the first snapshot, but a
        // click must visibly respond immediately (and the marker write is selection-diffed).
        if (selection.apply(s.selection)) viewer?.scene.requestRender?.(); // ring + border, diffed on change
        render(s);
      });
      const cur = store.getState();
      selection.set(cur.selection);
      if (cur.latest) render(cur);
    },

    applySnapshot(s): void {
      store.getState().applySnapshot(s); // canonical path: into the store → subscription renders
      if (!unsubscribe) render(store.getState()); // direct-drive fallback when not mounted/subscribed
    },

    setLayerVisible(layer, visible): void {
      layersVisible[layer] = visible;
      const st = store.getState();
      if (st.latest) applyFlows(st); // re-apply overlay so the toggled layer's flows clear immediately
    },

    setQuality(tier): void {
      applyQuality(tier); // shared with the dpr-change watcher (recomputes resolutionScale on zoom/monitor move)
    },

    setBasemap(kind, opts): void {
      basemap = kind;
      if (!viewer) return;
      if (kind === "photoreal3d") {
        // 3D Tiles are a Primitive, not an imagery layer — load (or re-show) the tileset & hide the
        // globe. Async + graceful: a missing key / load failure falls back to 2D without throwing.
        void enable3DTileset(opts?.apiKey);
        return;
      }
      // 2D imagery basemap: tear down any active 3D tileset, drop the grid lift, restore the globe + imagery.
      remove3DTileset();
      applyLift(false); // grid back onto the flat ground (no float over a flat basemap)
      viewer.scene.globe.show = true;
      viewer.scene.globe.depthTestAgainstTerrain = false; // Phase 2.6: off on flat ellipsoid
      viewer.imageryLayers.removeAll();
      viewer.imageryLayers.addImageryProvider(buildImageryProvider(cesium, kind, opts)); // the lone 2D network path
    },

    onPick(cb): void {
      pickCbs.push(cb);
    },

    dispose(): void {
      if (animId !== 0 && typeof cancelAnimationFrame === "function") cancelAnimationFrame(animId);
      animId = 0;
      dprCleanup?.(); // unhook the devicePixelRatio-change watcher
      dprCleanup = null;
      unsubscribe?.();
      unsubscribe = null;
      handler?.destroy();
      handler = null;
      remove3DTileset(); // drop the Google 3D tileset handle before the viewer goes
      lastSunDate = null;
      if (viewer && !viewer.isDestroyed()) viewer.destroy();
      viewer = null;
      edgeLines = null;
      edgeCollections.clear();
      flowMaterials.clear();
      staticMaterials.clear();
      idleMaterials.clear();
      selectionMat = null;
      selectedEdgeId = null;
      edgeTier.clear();
      edgeKv.clear();
      edgeRating.clear();
      flowState.clear();
      arcLines = null;
      infoArcs = null;
      energyGlyphs?.dispose();
      infoGlyphs?.dispose();
      for (const leg of financeLegs) leg.glyphs.dispose();
      financeLegs = [];
      energyGlyphs = infoGlyphs = null;
      edgePolyById.clear();
      markerPoints = null;
      selOutlineLines = null; // raw collection (destroyed with the viewer)
      selection.dispose(); // release the marker/outline closures + reset the border/diff state
      nodePos.clear();
      nodePoints = null;
      wallLayer = null;
      plantLayer = null;
      prosumerLayer = null;
      nodeAssetLayer = null;
      flowMaterial = null;
      footprintLayer = null;
      minorLines = null;
      cpPoints = null;
      facilityParcelLayer?.dispose();
      facilityParcelLayer = null;
      crisisBand = null;
      gridCentroid = null;
      liftActive = false;
      edgeIndex.clear();
      nodeIndex.clear();
      lastSnapshot = null;
    },

    _debug: {
      get lastSnapshot() { return lastSnapshot; },
      get quality() { return quality; },
      // P14 live-verification introspection (NON-contract): the histogram of material `type`s actually
      // applied to the edge polylines on the live map — proves the loading-danger band (`..._F_warn/hot/red`),
      // the dead-grey OFF (`..._OFF`), and the selection material (`PowerFlow_SEL`) are LIVE, not just
      // unit-tested — plus the selected edge id and the count of shown danger-halo billboards.
      edgeMaterials(): { tally: Record<string, number>; selected: string | null; haloShown: number; haloTotal: number } {
        const tally: Record<string, number> = {};
        for (const line of edgeIndex.values()) {
          const t = (line.material as { type?: string } | null)?.type ?? "(none)";
          tally[t] = (tally[t] ?? 0) + 1;
        }
        let haloShown = 0;
        let haloTotal = 0;
        const hc = energyGlyphs?.haloCollection as { length?: number; get?: (i: number) => { show?: boolean } } | null | undefined;
        if (hc && typeof hc.length === "number" && typeof hc.get === "function") {
          haloTotal = hc.length;
          for (let i = 0; i < hc.length; i++) { if (hc.get(i)?.show) haloShown++; }
        }
        return { tally, selected: selectedEdgeId, haloShown, haloTotal };
      },
      // Perf-HUD readout: the REAL render resolution. canvas.width/height are the drawing-buffer pixels
      // (already reflect resolutionScale × dpr), renderScale = buffer-px per CSS-px from the last tier,
      // dpr = the device pixel ratio. width/height are 0 before the first frame / under the test fake.
      renderResolution(): { width: number; height: number; renderScale: number; dpr: number } {
        const cv = viewer?.scene.canvas as { width?: number; height?: number } | undefined;
        const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
        return {
          width: typeof cv?.width === "number" ? cv.width : 0,
          height: typeof cv?.height === "number" ? cv.height : 0,
          renderScale: lastRenderScale,
          dpr,
        };
      },
      get basemap() { return basemap; },
      get edgeCount() { return edgeIndex.size; },
      get layersVisible() { return layersVisible; },
      lookAt(lon: number, lat: number, heightM: number): void {
        viewer?.camera.setView({ destination: cesium.Cartesian3.fromDegrees(lon, lat, heightM) });
        viewer?.scene.requestRender?.();
      },
      frameMs(samples = 30): number {
        const render = viewer?.scene.render?.bind(viewer.scene);
        if (!render) return -1;
        render(); render(); // warm: compile/upload anything pending so the loop times steady frames
        const t0 = performance.now();
        for (let i = 0; i < samples; i++) render();
        return Math.round(((performance.now() - t0) / samples) * 100) / 100;
      },
      setEffectsShow(visible: boolean): void {
        wallLayer?.setShow(visible);
        footprintLayer?.setShow(visible);
        plantLayer?.setShow(visible);
        prosumerLayer?.setShow(visible);
        viewer?.scene.requestRender?.();
      },
      get flowTime() { return flowMaterial ? flowMaterial.uniforms.time : -1; },
      // Batch C-2 — date/sun seam for the index.html clock UI. `simDate` is the calendar instant the
      // sun is currently using (derived from the last snapshot's season+hour + displayYear); the UI
      // renders its day/month/year + label from this. setDisplayYear re-stamps the year (cosmetic) and
      // re-pushes the sun. setLighting toggles globe lighting (sun on/off) without touching the clock.
      get simDate(): (SunDate & { label: string }) | null {
        return lastSunDate ? { ...lastSunDate, label: formatSunDate(lastSunDate) } : null;
      },
      get displayYear() { return displayYear; },
      setDisplayYear(year: number): void {
        displayYear = year;
        lastSunDate = null; // force a re-push on the next render even if season/hour are unchanged
        if (lastSnapshot) applySun(lastSnapshot);
        viewer?.scene.requestRender?.();
      },
      setLighting(on: boolean): void {
        if (viewer) viewer.scene.globe.enableLighting = on;
        viewer?.scene.requestRender?.();
      },
      // Phase 1.1: voltage overlay (partially wired — the toggle renders but the UI button is deferred)
      setVoltageOverlay(_on: boolean): void { /* deferred — node voltage recolour path not yet wired to UI */ },
      get voltageOverlay() { return voltageOverlay; },
      get gridLifted() { return liftActive; },
    },
  };
}
